import React, { useEffect } from 'react';
import './PipelineSection.css';

const BRAND_GRAD = 'linear-gradient(90deg,#6366F1,#8B5CF6 55%,#D946EF)';
const GRN = '#19D6A1';
const VIO = '#9A7BFF';
const VIO_GLOW = '0 0 14px rgba(154,123,255,0.85), 0 4px 10px rgba(0,0,0,0.4)';
const GRN_GLOW = '0 0 14px rgba(25,214,161,0.8), 0 4px 10px rgba(0,0,0,0.4)';

function OculoFlow() {
  const FW = 1180, FH = 540;

  const nodeBase = { position: 'absolute', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12, padding: '0 15px', borderRadius: 14, background: 'rgba(255,255,255,0.045)' };
  const titleS = { fontWeight: 700, fontSize: 15, fontFamily: 'Montserrat,sans-serif', color: '#fff', lineHeight: 1.12, whiteSpace: 'nowrap' };
  const subS   = { fontWeight: 500, fontSize: 11, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.03em', color: 'rgba(255,255,255,0.5)', marginTop: 3 };
  const iconTile = (col) => ({ width: 38, height: 38, flexShrink: 0, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: col });

  const pkt = (key, kf, dur, delay, raw, icon) => (
    <div key={key} style={{ position: 'absolute', left: 0, top: 0, width: 30, height: 30, borderRadius: 8, zIndex: 5, display: 'grid', placeItems: 'center', willChange: 'transform', background: raw ? VIO : GRN, boxShadow: raw ? VIO_GLOW : GRN_GLOW, animation: kf + ' ' + dur + 's linear ' + delay + 's infinite' }}>
      <i className={'bx ' + icon} style={{ fontSize: 15, color: raw ? '#1b1033' : '#06231b' }} />
    </div>
  );

  const srcDefs = [
    { y: 150, t: 'Contractor A', s: 'Off-site fabrication' },
    { y: 270, t: 'Contractor B', s: 'Field services' },
    { y: 390, t: 'Site & Crews', s: 'On-site inspection' },
  ];

  const sources = srcDefs.map((d, i) => (
    <div key={'s' + i} style={Object.assign({}, nodeBase, { left: 30, top: d.y - 42, width: 190, height: 84, border: '1px solid rgba(198,172,255,0.28)' })}>
      <div style={iconTile('rgba(99,102,241,0.12)')}>
        <i className="bx bx-buildings" style={{ fontSize: 21, color: '#C6ACFF' }} />
      </div>
      <div>
        <div style={titleS}>{d.t}</div>
        <div style={subS}>{d.s}</div>
      </div>
    </div>
  ));

  const docRows = [];
  for (let r = 0; r < 7; r++) {
    docRows.push(
      <div key={r} style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
        <div style={{ width: 16, height: 5, borderRadius: 2, background: 'rgba(19,16,34,0.32)' }} />
        <div style={{ flex: 1, height: 5, borderRadius: 2, background: 'rgba(19,16,34,0.16)' }} />
      </div>
    );
  }

  const storeGrid = [];
  for (let r = 0; r < 5; r++) {
    const cells = [];
    for (let c = 0; c < 4; c++) {
      const on = (r + c) % 3 === 0;
      cells.push(<div key={c} style={{ flex: 1, height: 12, borderRadius: 3, background: on ? 'rgba(25,214,161,0.5)' : 'rgba(255,255,255,0.10)', animation: on ? 'ocf-rowfill 2.6s ease-in-out ' + (r * 0.25) + 's infinite' : 'none' }} />);
    }
    storeGrid.push(<div key={r} style={{ display: 'flex', gap: 5, marginBottom: 6 }}>{cells}</div>);
  }

  const chevrons = [0, 1, 2].map((i) => (
    <i key={'ch' + i} className="bx bx-chevron-right" style={{ position: 'absolute', left: 548 + i * 16, top: 258, fontSize: 24, color: GRN, animation: 'ocf-chev 1.8s ease-in-out ' + (i * 0.3) + 's infinite', zIndex: 4 }} />
  ));

  const cliRows = [
    { icon: 'bx-shield-quarter', t: 'RBI / Integrity' },
    { icon: 'bx-server',         t: 'ERP systems' },
    { icon: 'bx-badge-check',    t: 'Quality management' },
  ];

  const lines = (
    <svg viewBox={'0 0 ' + FW + ' ' + FH} width={FW} height={FH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
      {srcDefs.map((d) => (
        <line key={'in' + d.y} x1={224} y1={d.y} x2={396} y2={270} stroke="rgba(198,172,255,0.22)" strokeWidth={2} strokeDasharray="4 6" />
      ))}
      <line x1={754} y1={270} x2={884} y2={270} stroke="rgba(25,214,161,0.45)" strokeWidth={2.4} strokeDasharray="7 7" style={{ animation: 'ocf-dash 1.4s linear infinite' }} />
      <polyline points="446,101 412,101 412,150" fill="none" stroke="rgba(129,107,255,0.5)" strokeWidth={1.5} />
      <polyline points="704,101 738,101 738,150" fill="none" stroke="rgba(129,107,255,0.5)" strokeWidth={1.5} />
    </svg>
  );

  const packets = [
    pkt('in1a', 'ocf-in1', 6, -0.5, true,  'bx-file'),
    pkt('in1b', 'ocf-in1', 6, -3.5, true,  'bx-file'),
    pkt('in2a', 'ocf-in2', 6, -1.8, true,  'bx-file'),
    pkt('in2b', 'ocf-in2', 6, -4.8, true,  'bx-file'),
    pkt('in3a', 'ocf-in3', 6, -2.6, true,  'bx-file'),
    pkt('in3b', 'ocf-in3', 6, -5.6, true,  'bx-file'),
    pkt('out1', 'ocf-out', 5, -1.0, false, 'bx-check'),
    pkt('out2', 'ocf-out', 5, -3.5, false, 'bx-check'),
  ];

  return (
    <div style={{ position: 'relative', width: FW, height: FH }}>
      <div style={{ position: 'absolute', top: 0, left: 114, transform: 'translateX(-50%)', fontWeight: 800, fontSize: 12, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>Contractors &amp; Locations</div>
      <div style={{ position: 'absolute', top: 0, left: 575, transform: 'translateX(-50%)', fontWeight: 800, fontSize: 12, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#816BFF', whiteSpace: 'nowrap' }}>Oculo · Extraction</div>
      <div style={{ position: 'absolute', top: 0, left: 1012, transform: 'translateX(-50%)', fontWeight: 800, fontSize: 12, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.2em', textTransform: 'uppercase', color: GRN, whiteSpace: 'nowrap' }}>Client Systems</div>

      {lines}
      {sources}

      <div style={{ position: 'absolute', left: 366, top: 150, width: 412, height: 240, zIndex: 2, borderRadius: 18, border: '1px solid rgba(129,107,255,0.34)', background: 'rgba(129,107,255,0.05)', boxShadow: 'inset 0 0 44px rgba(129,107,255,0.07)' }}>
        <span style={{ position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', padding: '3px 12px', borderRadius: 999, background: '#1a1430', border: '1px solid rgba(129,107,255,0.55)', fontWeight: 800, fontSize: 9, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#C6ACFF' }}>Oculo</span>
      </div>

      <div style={{ position: 'absolute', left: 396, top: 182, width: 138, height: 176, zIndex: 4, borderRadius: 10, overflow: 'hidden', padding: '13px 13px', border: '1px solid rgba(255,255,255,0.85)', background: 'linear-gradient(180deg,#ffffff,#eef1fb)', boxShadow: '0 30px 60px -24px rgba(0,0,0,0.6)' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 36, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(56,150,255,0), rgba(96,176,255,0.92), rgba(56,150,255,0))', boxShadow: '0 0 24px rgba(86,166,255,0.85)', animation: 'oc-beam 3.6s ease-in-out infinite', mixBlendMode: 'screen' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
          <div style={{ width: 50, height: 7, borderRadius: 3, background: 'rgba(99,102,241,0.6)' }} />
          <div style={{ width: 16, height: 16, borderRadius: 3, border: '1.5px solid rgba(19,16,34,0.25)' }} />
        </div>
        {docRows}
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <div style={{ width: '46%', height: 18, borderRadius: 3, background: 'rgba(19,16,34,0.08)' }} />
          <div style={{ width: '30%', height: 18, borderRadius: 3, background: 'rgba(2,167,131,0.18)' }} />
        </div>
      </div>
      <div style={{ position: 'absolute', left: 465, top: 366, transform: 'translateX(-50%)', fontWeight: 600, fontSize: 10, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>Scanned &amp; read</div>

      {chevrons}

      <div style={{ position: 'absolute', left: 604, top: 182, width: 150, height: 176, zIndex: 4, borderRadius: 12, padding: '14px 15px', border: '1px solid rgba(25,214,161,0.34)', background: 'linear-gradient(180deg, rgba(20,40,34,0.95), rgba(13,24,22,0.95))', boxShadow: '0 30px 60px -24px rgba(0,0,0,0.55)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <i className="bx bx-data" style={{ fontSize: 18, color: GRN }} />
          <span style={{ fontWeight: 700, fontSize: 12, fontFamily: 'Montserrat,sans-serif', color: '#fff' }}>Structured</span>
        </div>
        {storeGrid}
      </div>
      <div style={{ position: 'absolute', left: 679, top: 366, transform: 'translateX(-50%)', fontWeight: 600, fontSize: 10, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.07em', color: 'rgba(25,214,161,0.85)', whiteSpace: 'nowrap' }}>Normalised record</div>

      <div style={{ position: 'absolute', left: 575, top: 86, transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', gap: 9, padding: '6px 13px 6px 7px', borderRadius: 999, background: 'rgba(99,102,241,0.10)', border: '1px dashed rgba(129,107,255,0.5)', whiteSpace: 'nowrap', zIndex: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 9, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.13em', color: '#1a1430', background: '#C6ACFF', padding: '3px 7px', borderRadius: 999 }}>OPTIONAL</span>
        <i className="bx bx-bot" style={{ fontSize: 15, color: '#C6ACFF' }} />
        <span style={{ fontWeight: 700, fontSize: 11, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.04em', color: '#D8CCFF' }}>AI oversight &amp; validation</span>
      </div>

      <div style={{ position: 'absolute', left: 884, top: 162, width: 256, height: 216, zIndex: 6, borderRadius: 16, padding: '16px 18px', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(129,107,255,0.32)', boxShadow: '0 30px 70px -30px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, paddingBottom: 13, marginBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
          <div style={iconTile('rgba(99,102,241,0.14)')}>
            <i className="bx bx-been-here" style={{ fontSize: 22, background: 'var(--orbix-brand-gradient)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }} />
          </div>
          <div>
            <div style={titleS}>Client systems</div>
            <div style={subS}>Asset owner platforms</div>
          </div>
        </div>
        {cliRows.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
            <i className={'bx ' + c.icon} style={{ fontSize: 17, color: '#C6ACFF', width: 20 }} />
            <span style={{ fontWeight: 600, fontSize: 13, fontFamily: 'Manrope,sans-serif', color: 'rgba(255,255,255,0.82)' }}>{c.t}</span>
            <i className="bx bx-check" style={{ marginLeft: 'auto', fontSize: 16, color: GRN }} />
          </div>
        ))}
      </div>

      <div style={{ position: 'absolute', left: 1012, top: 392, transform: 'translateX(-50%)', fontWeight: 600, fontSize: 11, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.05em', color: 'rgba(25,214,161,0.8)', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
        <i className="bx bx-check-circle" style={{ fontSize: 14 }} />
        <span>No data entry · API</span>
      </div>
      <div style={{ position: 'absolute', left: 114, top: 444, transform: 'translateX(-50%)', fontWeight: 600, fontSize: 11, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.42)', whiteSpace: 'nowrap' }}>Reports · scans · images</div>

      {packets}
    </div>
  );
}

function PipelineSection() {
  useEffect(() => {
    const flowWrap  = document.getElementById('oc-flow-wrap');
    const flowScale = document.getElementById('oc-flow-scale');
    let roFlow;
    const fitFlow = () => {
      if (!flowWrap || !flowScale) return;
      const avail = flowWrap.clientWidth;
      const sc = Math.min(1, avail / 1180);
      flowScale.style.transform = 'scale(' + sc + ')';
      flowWrap.style.height = (540 * sc) + 'px';
    };
    if (flowWrap && flowScale) {
      window.addEventListener('resize', fitFlow);
      if (window.ResizeObserver) {
        roFlow = new ResizeObserver(fitFlow);
        roFlow.observe(flowWrap);
      }
      fitFlow();
      setTimeout(fitFlow, 200);
      setTimeout(fitFlow, 700);
      setTimeout(fitFlow, 1600);
    }
    return () => {
      window.removeEventListener('resize', fitFlow);
      if (roFlow) roFlow.disconnect();
    };
  }, []);

  return (
    <section style={{ position: 'relative', background: '#15102A', padding: '104px 56px 96px', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: '50%', top: '8%', transform: 'translateX(-50%)', width: 1300, height: 560, background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.16), rgba(99,102,241,0) 64%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', maxWidth: 1240, margin: '0 auto' }}>
        <div className="oc-reveal" style={{ textAlign: 'center', marginBottom: 40 }}>
          <span style={{ font: '700 12px Manrope,sans-serif', letterSpacing: '0.16em', color: '#816BFF' }}>ONE CANONICAL RECORD</span>
          <h2 style={{ margin: '14px 0 0', fontFamily: 'Montserrat,sans-serif', fontWeight: 800, fontSize: 38, letterSpacing: '-0.015em', color: '#fff' }}>
            Accelerate Data Ingestion Across Companies.
          </h2>
        </div>
        <div className="oc-reveal" id="oc-flow-wrap" style={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
          <div id="oc-flow-scale" style={{ transformOrigin: 'top center', flex: '0 0 auto' }}>
            <OculoFlow />
          </div>
        </div>
        <p className="oc-reveal oc-hide-sm" style={{ margin: '34px auto 0', maxWidth: 840, textAlign: 'center', fontFamily: 'Montserrat,sans-serif', fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.62)' }}>
          Oculo unifies reports from every contractor and location into one canonical, AI-validated record, then feeds your RBI, quality, and ERP systems with no data entry. It scales across every project.
        </p>
      </div>
    </section>
  );
}

export default PipelineSection;
